import React, { useMemo, useState } from "react";
import { useI18n } from "../../i18n";
import { categoryIcon } from "../utils/categories";
import { groupStats, lowStockLimit } from "../utils/groups";
import { productImageUrl } from "../utils/images";
import { formatAmount, round2 } from "../utils/money";
import { bestOfferFor } from "../utils/offers";
import Sheet from "./Sheet";

const Thumb = ({ src, category }) => {
  const [failed, setFailed] = useState(false);
  const url = failed ? null : productImageUrl(src);

  return (
    <span className="shrink-0 w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center overflow-hidden text-xl">
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        categoryIcon(category)
      )}
    </span>
  );
};

const Row = ({ member, groupImage, quantity, lowStockThreshold, onAdd, onRemove }) => {
  const { t } = useI18n();
  const isOut = member.stock_quantity <= 0;
  const isLow = !isOut && member.stock_quantity <= lowStockLimit(lowStockThreshold);

  // What one unit rings up at, so the row agrees with the ticket
  const offer = bestOfferFor(member, 1);
  const price = round2(Number(member.unit_price) - (offer ? offer.amount : 0));

  return (
    <li
      className={`flex items-stretch gap-2 rounded-2xl border transition-colors ${
        quantity > 0
          ? "border-emerald-400 bg-emerald-500/10"
          : "border-slate-800 bg-slate-900"
      } ${isOut ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        onClick={() => onAdd(member)}
        aria-label={t("pos.tile.add", {
          name: member.name,
          price: formatAmount(price),
        })}
        className="flex-1 min-w-0 flex items-center gap-3 p-2.5 text-start active:scale-[0.98] transition-transform"
      >
        <Thumb src={member.image || groupImage} category={member.category} />

        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold text-white truncate">
            {member.variant_label || member.name}
          </span>
          <span className="flex items-center gap-2 mt-0.5">
            {isOut ? (
              <span className="px-1.5 py-0.5 rounded-md bg-red-500/90 text-white text-[10px] font-bold uppercase tracking-wide">
                {t("pos.tile.out")}
              </span>
            ) : (
              <span
                className={`text-xs tabular-nums ${
                  isLow ? "text-amber-400 font-semibold" : "text-slate-400"
                }`}
              >
                {t("pos.tile.left", { count: member.stock_quantity })}
              </span>
            )}
            {member.offer && (
              <span className="px-1.5 py-0.5 rounded-md bg-amber-500 text-slate-900 text-[10px] font-bold">
                {t("pos.tile.offer")}
              </span>
            )}
          </span>
        </span>

        <span className="shrink-0 text-base font-bold text-emerald-400 tabular-nums">
          {formatAmount(price)}
        </span>
      </button>

      {quantity > 0 && (
        <div className="shrink-0 flex items-center gap-1.5 pe-2.5">
          <button
            type="button"
            onClick={() => onRemove(member)}
            aria-label={t("pos.group.removeOne", { name: member.variant_label || member.name })}
            className="w-9 h-9 rounded-full bg-slate-800 text-white text-lg leading-none hover:bg-slate-700 active:scale-90 transition"
          >
            −
          </button>
          <span className="min-w-6 text-center text-sm font-bold text-emerald-300 tabular-nums">
            {quantity}
          </span>
        </div>
      )}
    </li>
  );
};

/**
 * A range opened up: every flavour or colour with its own stock and price.
 * Stays open while the cashier adds several, since a customer buying two
 * flavours should not have to reopen it.
 */
const GroupSheet = ({
  group,
  members,
  quantityOf,
  lowStockThreshold,
  onAdd,
  onRemove,
  onClose,
}) => {
  const { t } = useI18n();

  // On the shelf first, then A–Z, so what can actually be sold is at the top
  const sorted = useMemo(
    () =>
      [...members].sort(
        (a, b) =>
          (a.stock_quantity <= 0) - (b.stock_quantity <= 0) ||
          (a.variant_label || a.name).localeCompare(b.variant_label || b.name),
      ),
    [members],
  );

  const stats = groupStats(members, lowStockThreshold);
  const inTicket = members.reduce((sum, member) => sum + quantityOf(member.id), 0);

  return (
    <Sheet
      open
      onClose={onClose}
      title={group.name}
      subtitle={t("pos.group.summary", { count: stats.count, stock: stats.total })}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="w-full h-12 rounded-2xl bg-primary-500 hover:bg-primary-400 text-white font-bold transition-colors"
        >
          {inTicket > 0
            ? t("pos.group.doneWithCount", { count: inTicket })
            : t("pos.group.done")}
        </button>
      }
    >
      <ul className="p-3 space-y-2">
        {sorted.map((member) => (
          <Row
            key={member.id}
            member={member}
            groupImage={group.image}
            quantity={quantityOf(member.id)}
            lowStockThreshold={lowStockThreshold}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        ))}
      </ul>
    </Sheet>
  );
};

export default GroupSheet;
